import React, {useEffect, useRef, useState} from 'react';
import {Badge, Checkbox, Col, Form, message, Row, Spin, Tabs} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {FileUploadUtility, WdiFileList} from '@/components/WdiControls/WdiFileUpload';
import TabPane from '@ant-design/pro-card/lib/components/TabPane';
import DynamicInformation from '../../components/DynamicInformation';
import {FormPageProps, WdiFormPageTitle} from '@/components/WdiBus/FormPage';
import {getFormStatusName, initApprovalPageContext, ViewEntityProps} from '../../components/data';
import {ResponseStatus} from '@/utils/request';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalProps, WdiStaffCard,} from '@/components/WdiControls';
import {Enum} from '@/enums/system';
import {FormSa, FormSaDetail} from '@/services/sales/sa';
import {SAWarningApprovalApp} from '../../components/WarningInformation';
import {FormSaDetailsForApprovalPanel, onDetailFilter,} from '../../PriceDiscountApply/components/Details';
import DataStore from '@/components/GridWidget/DataStore';
import {BOHQ} from '@/services/mdm/bohq';
import {DataOptions} from '@/services/basic/metadata';
import {CustSys} from '@/services/mdm/custsys';
import {CustType} from '@/services/mdm/custtype';
import {SoldToReal} from '@/services/mdm/soldtoreal';
import {ShipTo} from '@/services/mdm/shipto';
import {CodeOrNameTableApp} from '@/pages/sales/sa/PriceDiscountApply/components/CodeOrNameTableApp';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {RevenueType} from "@/services/mdm/revenuetype";
import {SalesAndCostRegion} from "@/pages/sales/sa/components/SalesAndCost";
import {HistoryReferencePanel} from "./HistoryReference";

const moreConfig: any = {
  successParams: [
    {
      url: '/basicapi/favorite/getButtons',
      keys: [],
    },
    {
      url: '/expenseapi/saButtons/get',
      keys: [
        'UndoButton',
        'ReeditButton',
        'CancelButton',
        'CopyButton',
        'InterveneFlowButton',
        'AppendExtNodeButton',
        'TransferNodeButton',
        'ClosedButton',
        'ViewProcessButton',
        'CancelPdButton',
        'ModifyPdPriceButton',
        'AddMailApproverButton',
        'FormSpPrintButton',
        'ExpediteFlowButton',
      ],
    },
  ],
  errorParams: [
    {
      url: '/expenseapi/saButtons/get',
      keys: ['RepairButton', 'InterveneFlowButton'],
    },
  ],
};

const approvalConfig: any = {
  params: {
    url: "/expenseapi/saButtons/get",
    keys: [
      "ApprovalButton",
      "ReadButton",
    ]
  }
};

const PriceDiscountApprovalApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [iniDataSource, setInitDataSource] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [warningCount, setWarningCount] = useState<number>(0);
  const [activeKey, setActiveKey] = useState<string>('1');
  const [erpSoldToRealDataSource, setErpSoldToRealDataSource] = useState<any[]>([]);
  const [shipToDataSource, setShipToDataSource] = useState<any[]>([]);
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});

  useEffect(() => {
    if (!formRef?.current) {
      return;
    }
    setActiveKey(props.data?.defaultActiveKey ?? '1');
  }, [!!formRef?.current]);
  const handleOnClick = (dataSource: any[], title?: string) => {
    setModalProps({visible: true, data: dataSource, title: title});
  };
  const onClose = () => {
    setModalProps({visible: false});
  }


  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApprovalPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;
        if (viewEntity.formSa) {
          const soldToRealCount = (viewEntity.formSa?.soldToRealNames?.split(',') ?? []).length;
          const shipToCount = (viewEntity.formSa?.shipToNames?.split(',') ?? []).length;
          formRef.current?.setFieldsValue({
            activityName: viewEntity.formSa?.activityName,
            ActivityDate: wdiCore.Date.Join(viewEntity.formSa?.activityStartDate, viewEntity.formSa?.activityEndDate),
            SupplyDate: wdiCore.Date.Join(viewEntity.formSa?.supplyStartDate, viewEntity.formSa?.supplyEndDate),
            boNames: viewEntity.formSa?.boNames,
            soldToRealNames: soldToRealCount > 1 ? soldToRealCount + "个客户" : viewEntity.formSa?.soldToRealNames,
            shipToNames: shipToCount > 1 ? shipToCount + "个收货地" : viewEntity.formSa?.shipToNames,
            soldToRealNamesExclude: viewEntity.formSa?.soldToRealNamesExclude,
            isOld: viewEntity.formSa?.isOld,
            fileInfo: FileUploadUtility.parse(viewEntity.formSa?.fileName, viewEntity.formSa?.filePath),
            remark: viewEntity.formSa?.remark,
          });


          BOHQ.get(Number(viewEntity.formSa?.bohqId)).then((response) => {
            formRef.current?.setFieldsValue({
              bohqName: response.data.bohqName
            })
          });
          DataOptions.search({typeGroup: 122, value: viewEntity.formSa?.applyScopeId}).then((response) => {
            if (response.data?.length > 0) {
              formRef.current?.setFieldsValue({
                applyScopeName: response.data[0].name
              })
            }
          });
          if (viewEntity.formSa.custTypeId) {
            const custTypeResp = await CustType.get(viewEntity.formSa.custTypeId);
            formRef.current?.setFieldsValue({
              custTypeName: custTypeResp.data.codeOrName
            })
          }
          if (viewEntity.formSa.custSysCode) {
            const custSysResp = await CustSys.get(viewEntity.formSa.custSysCode);
            formRef.current?.setFieldsValue({
              custSysName: custSysResp?.data?.codeOrName,
            });
          }
          if (viewEntity.formSa.soldToRealCodes) {
            SoldToReal.search({soldToRealCodes: viewEntity.formSa.soldToRealCodes}).then(
              (result) => {
                const newDataSource = result.data?.map((item) => ({
                  codeOrName: item.codeOrName,
                }));
                setErpSoldToRealDataSource(DataStore.init(newDataSource));
              },
            );
          }
          if (viewEntity.formSa.shipToCodes) {
            ShipTo.search({shipToCodes: viewEntity.formSa.shipToCodes}).then((result) => {
              const newDataSource = result.data?.map((item) => ({
                codeOrName: item.codeOrName,
              }));
              setShipToDataSource(DataStore.init(newDataSource));
            });
          }

          const formSaId = viewEntity.formSa?.formSaId ?? 0;

          FormSa.Buttons.countOfWarning(formSaId).then(response => {
            setWarningCount(response.data);
          });
          const formSaDetailResp = await FormSaDetail.getsEx(formSaId);
          formSaDetailResp.data?.forEach((item) => {
            if (item.activityStartDate == null) {
              item.activityStartDate = viewEntity.formSa?.activityStartDate?.toString();
            }
            if (item.activityEndDate == null) {
              item.activityEndDate = viewEntity.formSa?.activityEndDate?.toString();
            }
            if (item.supplyStartDate == null) {
              item.supplyStartDate = viewEntity.formSa?.supplyStartDate?.toString();
            }
            if (item.supplyEndDate == null) {
              item.supplyEndDate = viewEntity.formSa?.supplyEndDate?.toString();
            }
          });

          setDataSource((await onDetailFilter(DataStore.init(formSaDetailResp.data))));
          setInitDataSource(DataStore.init(formSaDetailResp.data));

          if (formSaDetailResp.data[0].revenueType) {
            const revenueTypeResp = await RevenueType.get(formSaDetailResp.data[0].revenueType);
            formRef.current?.setFieldsValue({revenueTypeName: revenueTypeResp.data.revenueTypeName});
          }
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = `随货类活动申请单【${entity.form?.formNo}】`;
      getFormStatusName(entity).then(response => {
        title += `,${response}`;
        if (entity?.form?.relationType === 2) {
          title += '（变更申请）';
        }
        if (props.setPageTitle) props.setPageTitle(
          <WdiFormPageTitle
            title={title}
            onClose={props.onClose}
            callback={props.callback}
            form={entity?.form}
            useCaseId={Enum.UseCase.SA}
            moreConfig={moreConfig}
            approvalConfig={approvalConfig}/>
        );
      });
    }
  }, [props.setPageTitle, entity]);

  return (
    <>
      <WdiStaffCard formId={entity?.form?.formId}/>
      <Tabs activeKey={activeKey} onTabClick={(key, _) => setActiveKey(key)} size="large">
        <TabPane tab={<span>活动信息</span>} key="1">
          <ProForm
            formRef={formRef}
            submitter={{
              render: () => {
                return <></>;
              },
            }}
          >
            <WdiPanel
              title={<WdiCardTitle iconType={Enum.IconType.BASE_INFO} title="活动基本信息"/>}
              collapsed={true}
            >
              <Row gutter={[16, 0]}>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="activityName" label="活动名称">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="ActivityDate" label="活动期间">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="SupplyDate" label="供货期间">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="bohqName" label="预算归属">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="applyScopeName" label="适用范围类型">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="boNames" label="使用部门">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="custTypeName" label="客户分类">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="custSysName" label="客户组">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                {(entity?.formSa?.applyScopeId == Enum.AdoptScope.Custom ||
                  entity?.formSa?.applyScopeId == Enum.AdoptScope.CrossReginCustom) && (
                  <Col className="gutter-row" span={4}>
                    <div style={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}>
                      <Form.Item name="soldToRealNames" label="ERP客户" style={{flex: 1}}>
                        <WdiInput readOnly/>
                      </Form.Item>
                      <a
                        style={{marginLeft: '5px', whiteSpace: 'nowrap'}}
                        onClick={() => {
                          handleOnClick(erpSoldToRealDataSource, 'ERP客户');
                        }}
                      >
                        查看
                      </a>
                    </div>
                  </Col>
                )}
                {(entity?.formSa?.applyScopeId == Enum.AdoptScope.Custom ||
                  entity?.formSa?.applyScopeId == Enum.AdoptScope.CrossReginCustom) && (
                  <Col className="gutter-row" span={4}>
                    <div style={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}>
                      <Form.Item name="shipToNames" label="收货地" style={{flex: 1}}>
                        <WdiInput readOnly/>
                      </Form.Item>
                      <a
                        style={{marginLeft: '5px', whiteSpace: 'nowrap'}}
                        onClick={() => {
                          handleOnClick(shipToDataSource, '收货地');
                        }}
                      >
                        查看
                      </a>
                    </div>
                  </Col>
                )}
                {(entity?.formSa?.applyScopeId == Enum.AdoptScope.CustomType) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="soldToRealNamesExclude" label="排除ERP客户">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                <Col className="gutter-row" span={4}>
                  <Form.Item name="isOld" valuePropName="checked" label="是否积压品">
                    <Checkbox disabled={true}/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    name="revenueTypeName"
                    label="核算类型"
                    rules={[{required: true, message: '核算类型不能为空!'}]}
                  >
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 24]}>
                <Col className="gutter-row" span={16}>
                  <Form.Item name="remark" label="方案简述">
                    <WdiEditor readOnly={true}/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={8}>
                  <Form.Item
                    name="fileInfo"
                    label="附件"
                    valuePropName="fileList"
                    getValueFromEvent={(e: any) => {
                      return e.fileList || [];
                    }}
                  >
                    <WdiFileList
                      fileList={FileUploadUtility.parse(
                        entity?.formSa?.fileName,
                        entity?.formSa?.filePath,
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </WdiPanel>
          </ProForm>
          <FormSaDetailsForApprovalPanel
            data={{ecFieldConfigs: entity?.ecFieldConfigs, form: entity?.form}}
            initDataSource={iniDataSource}
            dataSource={dataSource}
            onChange={(data: any[]) => {
              setDataSource(data);
            }}
          />
          <HistoryReferencePanel data={{formNo: entity?.form?.formNo, formSaId: entity?.formSa?.formSaId}}/>
        </TabPane>
        <TabPane
          tab={
            <>
              <span>警示信息</span>
              <Badge
                count={warningCount}
                style={{
                  color: 'black',
                  boxShadow: 'none',
                  background: 'none',
                  top: '-20px',
                  right: '-15px',
                  position: 'absolute',
                }}
              ></Badge>
            </>
          }
          key="2"
        >
          <SAWarningApprovalApp formId={entity?.form?.formId}/>
        </TabPane>
        {entity?.boLevelRegionOpts && entity?.boLevelRegionOpts.length > 0 && (
          <TabPane tab={<span>销量与费用(区域)</span>} key="3-2">
            <SalesAndCostRegion
              formSa={entity?.formSa}
              formSaDetails={dataSource}
              boLevelOpts={entity?.boLevelRegionOpts}
            />
          </TabPane>
        )}
        <TabPane tab={<span>动态信息</span>} key="3">
          {entity?.form ? (
            <DynamicInformation
              formId={entity?.form?.formId}
              formTypeId={entity?.form?.formTypeId}
              pageTypeId={entity?.form?.pageTypeId}
              useCaseId={Enum.UseCase.SA}
            />
          ) : (
            <Spin/>
          )}
        </TabPane>
      </Tabs>
      {modalProps && modalProps.visible && <CodeOrNameTableApp {...modalProps} onClose={onClose}/>}
    </>
  );
};

export default PriceDiscountApprovalApp;
